import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, Upload, Save, Sparkles, Copy, Check, Globe, Plus, Trash2,
    ArrowUp, ArrowDown, Star, X, Disc3,
} from "lucide-react";
import { CoverImage } from "@/components/CoverImage";
import { inputCls, btnPrimary, btnGhost, btnDanger, formatErr } from "@/components/admin/ui";
import { spotifyEmbed } from "@/components/ReleasePlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", type: "Album", tracks: "", description: "", spotify: "", apple: "", amazon: "", youtube: "" };

const toBody = (form) => ({
    title: form.title,
    type: form.type,
    tracks: form.type === "Album" && form.tracks !== "" ? Number(form.tracks) : null,
    description: form.description || null,
    spotify: form.spotify || null,
    apple: form.apple || null,
    amazon: form.amazon || null,
    youtube: form.youtube || null,
});

const ReleaseFields = ({ form, setForm, testidPrefix }) => {
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    return (
        <>
            <div className="grid sm:grid-cols-3 gap-3">
                <input
                    required
                    value={form.title}
                    onChange={set("title")}
                    placeholder="Release title"
                    data-testid={`${testidPrefix}-title-input`}
                    className={inputCls}
                />
                <select value={form.type} onChange={set("type")} data-testid={`${testidPrefix}-type-select`} className={`${inputCls} appearance-none`}>
                    <option value="Album" className="bg-zinc-900">Album</option>
                    <option value="Single" className="bg-zinc-900">Single</option>
                </select>
                <input
                    type="number"
                    min="1"
                    value={form.type === "Album" ? form.tracks : ""}
                    onChange={set("tracks")}
                    disabled={form.type !== "Album"}
                    placeholder="Track count"
                    data-testid={`${testidPrefix}-tracks-input`}
                    className={`${inputCls} disabled:opacity-40`}
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input value={form.spotify} onChange={set("spotify")} placeholder="Spotify link — also powers the on-site player" data-testid={`${testidPrefix}-spotify-input`} className={inputCls} />
                <input value={form.youtube} onChange={set("youtube")} placeholder="YouTube link — player only when there's no Spotify link" data-testid={`${testidPrefix}-youtube-input`} className={inputCls} />
                <input value={form.apple} onChange={set("apple")} placeholder="Apple Music link" data-testid={`${testidPrefix}-apple-input`} className={inputCls} />
                <input value={form.amazon} onChange={set("amazon")} placeholder="Amazon Music link" data-testid={`${testidPrefix}-amazon-input`} className={inputCls} />
            </div>
            <p data-testid={`${testidPrefix}-player-note`} className="mt-2 font-meta text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                {spotifyEmbed(form.spotify)
                    ? form.youtube
                        ? "Plays the Spotify player, with a Watch on YouTube link under it"
                        : "Plays the Spotify player on the site"
                    : form.youtube
                        ? "Plays the YouTube video on the site"
                        : "No player yet — add a Spotify or YouTube link"}
            </p>
            <textarea
                rows={3}
                value={form.description}
                onChange={set("description")}
                placeholder="Description shown on the Music card and the Home feature block"
                data-testid={`${testidPrefix}-description-input`}
                className={`${inputCls} mt-3 resize-none`}
            />
        </>
    );
};

const AddReleaseForm = ({ token, onAdded }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`${API}/releases`, toBody(form), { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`"${form.title}" is live on the Music page.`);
            setForm(EMPTY);
            setOpen(false);
            onAdded();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not add the release."));
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} data-testid="admin-release-add-open" className={btnPrimary}>
                <Plus size={13} /> Add Release
            </button>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="admin-release-add-form"
            className="w-full rounded-2xl border border-amber-500/30 bg-zinc-900/70 backdrop-blur-md p-5 sm:p-6"
        >
            <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-4">New release</p>
            <ReleaseFields form={form} setForm={setForm} testidPrefix="admin-release-add" />
            <div className="flex flex-wrap gap-3 mt-4">
                <button type="submit" disabled={saving} data-testid="admin-release-add-submit" className={btnPrimary}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    {saving ? "Saving…" : "Save Release"}
                </button>
                <button type="button" onClick={() => setOpen(false)} data-testid="admin-release-add-cancel" className={btnGhost}>
                    <X size={13} /> Cancel
                </button>
            </div>
            <p className="text-xs text-zinc-500 mt-3">Upload the cover art after saving, from the release's own card.</p>
        </form>
    );
};

const AiCopyPanel = ({ release, form, token, onPublish }) => {
    const [kind, setKind] = useState("description");
    const [notes, setNotes] = useState("");
    const [output, setOutput] = useState("");
    const [generating, setGenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [copied, setCopied] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const generate = async () => {
        setGenerating(true);
        setOutput("");
        try {
            const { data } = await axios.post(`${API}/releases/${release.id}/ai-copy`, { kind, notes: notes || null }, { headers });
            setOutput(data.text);
            toast.success("Draft ready — review and edit before publishing.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "AI generation failed."));
        } finally {
            setGenerating(false);
        }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await axios.put(`${API}/releases/${release.id}`, { ...toBody(form), description: output }, { headers });
            toast.success(`Description live on the site for "${release.title}".`);
            onPublish(output);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not publish."));
        } finally {
            setPublishing(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(output);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Copy failed — select the text manually.");
        }
    };

    return (
        <div className="mt-4 rounded-xl border border-amber-500/25 bg-black/40 p-4" data-testid={`ai-copy-panel-${release.slug}`}>
            <div className="flex flex-wrap items-center gap-2">
                {["description", "caption"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setKind(k)}
                        data-testid={`ai-kind-${k}-${release.slug}`}
                        className={`text-[11px] font-meta tracking-wider uppercase rounded-full px-3 py-1.5 border transition-all duration-200 ${
                            kind === k
                                ? "bg-amber-500 text-black border-transparent font-semibold"
                                : "text-zinc-400 border-amber-500/25 hover:text-amber-200"
                        }`}
                    >
                        {k === "description" ? "Site Description" : "Social Caption"}
                    </button>
                ))}
            </div>
            <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context for Claude — theme, scripture, mood…"
                data-testid={`ai-notes-${release.slug}`}
                className={`${inputCls} mt-3`}
            />
            <button onClick={generate} disabled={generating} data-testid={`ai-generate-${release.slug}`} className={`${btnPrimary} mt-3`}>
                {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {generating ? "Claude is writing…" : "Generate with Claude"}
            </button>
            {output && (
                <div className="mt-4">
                    <textarea
                        value={output}
                        onChange={(e) => setOutput(e.target.value)}
                        rows={3}
                        data-testid={`ai-output-${release.slug}`}
                        className={`${inputCls} resize-none`}
                    />
                    <div className="flex flex-wrap gap-3 mt-3">
                        {kind === "description" && (
                            <button onClick={publish} disabled={publishing || !output.trim()} data-testid={`ai-publish-${release.slug}`} className={btnPrimary}>
                                {publishing ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />}
                                Publish to Site
                            </button>
                        )}
                        <button onClick={copy} data-testid={`ai-copy-${release.slug}`} className={btnGhost}>
                            {copied ? <Check size={13} /> : <Copy size={13} />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ReleaseRow = ({ release, token, canMoveUp, canMoveDown, onMove, onChanged }) => {
    const [form, setForm] = useState({
        title: release.title,
        type: release.type,
        tracks: release.tracks ?? "",
        description: release.description || "",
        spotify: release.links.spotify || "",
        apple: release.links.apple || "",
        amazon: release.links.amazon || "",
        youtube: release.links.youtube || "",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const fileRef = useRef(null);
    const headers = { Authorization: `Bearer ${token}` };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/releases/${release.id}`, toBody(form), { headers });
            toast.success(`Saved "${form.title}".`);
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save the release."));
        } finally {
            setSaving(false);
        }
    };

    const uploadCover = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const data = new FormData();
            data.append("file", file);
            await axios.post(`${API}/releases/${release.id}/cover`, data, { headers });
            toast.success(`Cover uploaded for "${release.title}".`);
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Cover upload failed."));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const markLatest = async () => {
        try {
            await axios.post(`${API}/releases/${release.id}/featured`, {}, { headers });
            toast.success(`"${release.title}" is now the Latest Release on Home.`);
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not update."));
        }
    };

    const remove = async () => {
        try {
            await axios.delete(`${API}/releases/${release.id}`, { headers });
            toast.success(`"${release.title}" removed from the site.`);
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not delete."));
        }
    };

    return (
        <div
            data-testid={`admin-release-row-${release.slug}`}
            className="rounded-2xl border border-amber-500/20 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-6 grid md:grid-cols-[140px_1fr] gap-5"
        >
            <div className="w-36 h-36 md:w-[140px] md:h-[140px] rounded-xl overflow-hidden border border-amber-500/20">
                <CoverImage release={release} className="w-full h-full object-cover" />
            </div>
            <div>
                <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-lg font-bold text-white">{release.title}</h3>
                    {release.featured && (
                        <span
                            data-testid={`admin-release-latest-badge-${release.slug}`}
                            className="inline-flex items-center gap-1.5 font-meta text-[10px] tracking-[0.2em] uppercase bg-amber-500 text-black font-bold rounded-full px-2.5 py-1"
                        >
                            <Star size={10} fill="currentColor" /> Latest Release
                        </span>
                    )}
                </div>

                <div className="mt-4">
                    <ReleaseFields form={form} setForm={setForm} testidPrefix={`admin-release-${release.slug}`} />
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadCover} data-testid={`admin-cover-file-${release.slug}`} />
                    <button onClick={save} disabled={saving} data-testid={`admin-save-${release.slug}`} className={btnPrimary}>
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`admin-cover-upload-${release.slug}`} className={btnGhost}>
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading ? "Uploading…" : "Upload Cover"}
                    </button>
                    {!release.featured && (
                        <button onClick={markLatest} data-testid={`admin-mark-latest-${release.slug}`} className={btnGhost}>
                            <Star size={13} /> Mark Latest
                        </button>
                    )}
                    <button
                        onClick={() => setAiOpen((o) => !o)}
                        data-testid={`admin-ai-toggle-${release.slug}`}
                        className={
                            aiOpen
                                ? "inline-flex items-center gap-2 text-xs font-meta tracking-wider uppercase rounded-full px-4 py-2.5 bg-amber-500 text-black border border-transparent font-semibold"
                                : btnGhost
                        }
                    >
                        <Sparkles size={13} /> AI Copy
                    </button>
                    <button onClick={() => onMove("up")} disabled={!canMoveUp} data-testid={`admin-release-up-${release.slug}`} className={`${btnGhost} px-3`} aria-label="Move up">
                        <ArrowUp size={13} />
                    </button>
                    <button onClick={() => onMove("down")} disabled={!canMoveDown} data-testid={`admin-release-down-${release.slug}`} className={`${btnGhost} px-3`} aria-label="Move down">
                        <ArrowDown size={13} />
                    </button>
                    {confirming ? (
                        <>
                            <button onClick={remove} data-testid={`admin-release-confirm-delete-${release.slug}`} className={btnDanger}>
                                <Trash2 size={13} /> Yes, delete
                            </button>
                            <button onClick={() => setConfirming(false)} data-testid={`admin-release-cancel-delete-${release.slug}`} className={btnGhost}>
                                Keep
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setConfirming(true)} data-testid={`admin-release-delete-${release.slug}`} className={btnDanger}>
                            <Trash2 size={13} /> Delete
                        </button>
                    )}
                </div>

                {aiOpen && (
                    <AiCopyPanel
                        release={release}
                        form={form}
                        token={token}
                        onPublish={(text) => {
                            setForm((f) => ({ ...f, description: text }));
                            onChanged();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export const ReleasesManager = ({ token }) => {
    const [releases, setReleases] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    const load = () => {
        axios
            .get(`${API}/releases`)
            .then((res) => setReleases(res.data))
            .catch(() => setReleases([]));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const move = async (release, direction) => {
        const list = [...releases];
        const index = list.findIndex((r) => r.id === release.id);
        const neighbours = direction === "up"
            ? list.slice(0, index).filter((r) => r.type === release.type)
            : list.slice(index + 1).filter((r) => r.type === release.type);
        const target = direction === "up" ? neighbours[neighbours.length - 1] : neighbours[0];
        if (!target) return;
        const targetIndex = list.findIndex((r) => r.id === target.id);
        [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
        setReleases(list);
        try {
            const { data } = await axios.put(`${API}/releases/reorder`, { ids: list.map((r) => r.id) }, { headers });
            setReleases(data);
        } catch {
            toast.error("Could not save the new order.");
            load();
        }
    };

    const sections = [
        { title: "Albums", type: "Album" },
        { title: "Singles", type: "Single" },
    ];

    return (
        <section data-testid="admin-releases-tab">
            <AddReleaseForm token={token} onAdded={load} />

            {releases === null ? (
                <div className="mt-6 rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-40 animate-pulse" />
            ) : releases.length === 0 ? (
                <div className="mt-6 rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 p-10 text-center" data-testid="admin-releases-empty">
                    <Disc3 size={28} className="mx-auto text-amber-500/60" />
                    <p className="text-sm text-zinc-400 mt-3">No releases yet. Add your first album or single above.</p>
                </div>
            ) : (
                sections.map((section) => {
                    const items = releases.filter((r) => r.type === section.type);
                    if (items.length === 0) return null;
                    return (
                        <div key={section.type} className="mt-10" data-testid={`admin-releases-section-${section.type.toLowerCase()}`}>
                            <h2 className="font-display text-lg font-bold text-white flex items-center gap-4 mb-5">
                                {section.title}
                                <span className="font-meta text-[11px] tracking-[0.3em] text-amber-500/70 font-normal">
                                    {String(items.length).padStart(2, "0")}
                                </span>
                                <span className="flex-1 h-px bg-gradient-to-r from-amber-500/40 to-transparent" />
                            </h2>
                            <div className="space-y-5">
                                {items.map((r, i) => (
                                    <ReleaseRow
                                        key={r.id}
                                        release={r}
                                        token={token}
                                        canMoveUp={i > 0}
                                        canMoveDown={i < items.length - 1}
                                        onMove={(dir) => move(r, dir)}
                                        onChanged={load}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })
            )}
        </section>
    );
};

export default ReleasesManager;
